import styles from '@sicredi/styles/_select.scss';
import cn from 'clsx';
import Downshift, {
  Actions,
  ControllerStateAndHelpers,
  DownshiftProps,
  PropGetters,
  StateChangeOptions,
} from 'downshift';
import * as React from 'react';

import { Icon } from '../icon';
import { default as Input } from '../input/Input';
import { default as MaskedInput } from '../masked-input/MaskedInput';
import { Highlighter } from '../shared/highlighter';
import { NullableString } from '../shared/highlighter/Highlighter';
import { stripTags } from '../utils';
import { InputProps, Item, Props as SelectProps } from './Select';

export interface CustomDownshiftProps<Item>
  extends PropGetters<Item>,
    DownshiftProps<Item>,
    Pick<Actions<Item>, Exclude<keyof Actions<Item>, 'itemToString'>> {}

const Single: React.FC<SelectProps> = ({
  data,
  name,
  label,
  value,
  onBlur,
  loading,
  onClick,
  onFocus,
  onChange,
  className,
  renderItem,
  inputProps,
  maxRenderedItems,
  ...props
}) => {
  const [isFiltering, setFiltering] = React.useState(false);
  const isAsync = loading !== undefined;
  const [isOpen, setIsOpen] = React.useState(false);

  function getSelectedItem() {
    return value !== undefined
      ? data.find(item => item.value === value) || null
      : undefined;
  }

  function handleChange(item: Item) {
    onChange && onChange(item ? item.value : '');
  }

  function handleStateChange(
    options: StateChangeOptions<Item>,
    stateAndHelpers: ControllerStateAndHelpers<Item>
  ) {
    switch (options.type) {
      case Downshift.stateChangeTypes.keyDownArrowUp:
      case Downshift.stateChangeTypes.keyDownArrowDown:
      case Downshift.stateChangeTypes.unknown:
        setIsOpen(true);
        break;
      case Downshift.stateChangeTypes.mouseUp:
      case Downshift.stateChangeTypes.blurInput:
      case Downshift.stateChangeTypes.clickItem:
      case Downshift.stateChangeTypes.keyDownEnter:
        setIsOpen(false);
        break;
      case Downshift.stateChangeTypes.changeInput:
        setFiltering(true);
        setIsOpen(true);
        break;
    }

    if (!stateAndHelpers.isOpen) setFiltering(false);
  }
  function enhanceOnFocus(
    onFocus: InputProps['onFocus'],
    downshiftProps: CustomDownshiftProps<Item>
  ) {
    return (e: React.FocusEvent<HTMLInputElement>) => {
      !isAsync && downshiftProps.openMenu();

      onFocus && onFocus(e);
    };
  }

  function enhanceOnClick(
    onClick: InputProps['onClick'],
    downshiftProps: CustomDownshiftProps<Item>
  ) {
    return (e: React.MouseEvent<HTMLInputElement>) => {
      !isAsync && downshiftProps.openMenu();

      onClick && onClick(e);
    };
  }

  function enhanceOnBlur(
    onBlur: InputProps['onBlur'],
    downshiftProps: CustomDownshiftProps<Item>
  ) {
    return (e: React.FocusEvent<HTMLInputElement>) => {
      !e.target.value && downshiftProps.clearSelection();

      onBlur && onBlur(e);
    };
  }

  function filterByValue(data: Item[], value: NullableString) {
    return data.filter(
      item =>
        item.label &&
        String(item.label)
          .toLowerCase()
          .includes((value || '').toLowerCase())
    );
  }

  function renderItems(downshiftProps: CustomDownshiftProps<Item>) {
    if (isAsync && !data.length) {
      return loading ? (
        <li
          data-item=""
          style={{ pointerEvents: 'none' }}
          role="option"
          aria-selected="false"
        >
          Carregando...
        </li>
      ) : null;
    }

    let enhancedData =
      !isFiltering || isAsync
        ? data
        : filterByValue(data, downshiftProps.inputValue);

    if (!enhancedData.length) {
      return (
        <li data-item="" style={{ pointerEvents: 'none' }}>
          Não foi possível encontrar resultados para{' '}
          <span className={styles['not-found-text']} >{downshiftProps.inputValue}</span>
        </li>
      );
    }

    if (maxRenderedItems) {
      enhancedData = enhancedData.slice(0, maxRenderedItems);
    }

    const selectedValue =
      value !== undefined
        ? value
        : downshiftProps.selectedItem && downshiftProps.selectedItem.value;

    return enhancedData.map((item, index) => (
      <li
        key={item.value}
        data-item=""
        className={cn({
          [styles['-highlighted']]:
            selectedValue === item.value ||
            downshiftProps.highlightedIndex === index,
        })}
        {...downshiftProps.getItemProps({
          item,
          index,
        })}
      >
        <Highlighter text={downshiftProps.inputValue}>{item.label}</Highlighter>
      </li>
    ));
  }

  const InputComponent = inputProps && inputProps['mask'] ? MaskedInput : Input;

  return (
    <Downshift
      id={`${name}-input`}
      menuId={`${name}-input-items`}
      inputId={`${name}-input`}
      labelId={`${name}-input-label`}
      onChange={handleChange}
      itemToString={(item: Item) => (item ? stripTags(item.label) : '')}
      selectedItem={getSelectedItem()}
      onStateChange={handleStateChange}
      isOpen={isOpen}
    >
      {(downshiftProps: CustomDownshiftProps<Item>) => (
        <div
          className={cn(
            styles['sicredi-select'],
            {
              [styles['-async']]: isAsync,
            },
            className
          )}
        >
          <InputComponent
            icon={
              !isAsync ? (
                <Icon
                  name="chevron-down"
                  aria-hidden="true"
                  className={styles['icon']}
                />
              ) : (
                <Icon
                  name="search"
                  aria-hidden="true"
                  className={styles['icon']}
                />
              )
            }
            name={name}
            type="search"
            label={label}
            {...(downshiftProps.getInputProps({
              ...props,
              ...inputProps,
              onBlur: enhanceOnBlur(onBlur, downshiftProps),
              onClick: enhanceOnClick(onClick, downshiftProps),
              onFocus: enhanceOnFocus(onFocus, downshiftProps),
            }) as InputProps)}
          />

          <ul
            className={cn(styles['sicredi-field-list'], className)}
            data-items=""
            aria-hidden={downshiftProps.isOpen ? 'false' : 'true'}
            {...downshiftProps.getMenuProps({
              id: `${name}-input-items`,
              className: styles['items'],
            })}
          >
            {renderItems(downshiftProps)}
          </ul>
        </div>
      )}
    </Downshift>
  );
};

export default Single;
